// Patient intake routing.
//
// The main site-wide "Start now" CTA no longer routes directly to a single
// external Bask intake URL. Instead, it sends the patient to the internal
// "Choose Your Treatment" page where they select the correct treatment area
// first. Each treatment card on that page then routes into the product-
// specific Bask intake link defined in PRODUCT_INTAKE_URLS below.
//
// This intentionally prevents patients from landing in a general intake or
// the wrong product intake.

export const CHOOSE_TREATMENT_PATH = "/choose-your-treatment";

// Per-product Bask intake links. Fill each value in with the exact Bask URL
// for that product as it becomes available. Empty string = link not yet
// provided; the button will remain labeled "Start Now" but the click will
// be a safe no-op (no fallback to a general intake, per compliance).
export const PRODUCT_INTAKE_URLS = {
  "weight-loss": "https://start.melbiwellness.com/start-online-visit/weightloss", // GLP-1
  "glp-nad-bundles": "https://start.melbiwellness.com/start-online-visit/glp-nad-bundles", // GLP-1 + NAD+ bundle
  vitality: "https://start.melbiwellness.com/start-online-visit/nad",             // NAD+ Injection
  recovery: "https://start.melbiwellness.com/start-online-visit/sermorelin",      // Sermorelin
  antioxidant: "https://start.melbiwellness.com/start-online-visit/glutathione",  // Glutathione
  "metabolic-support": "https://start.melbiwellness.com/start-online-visit/mic",  // MIC + B12
  energy: "https://start.melbiwellness.com/start-online-visit/b12",               // B12
  "skin-care": "https://start.melbiwellness.com/start-online-visit/ghk",          // GHK-Cu
  "nad-nasal-spray": "https://start.melbiwellness.com/start-online-visit/nad",    // NAD+ Nasal Spray
};

// Kept for backwards compatibility with any older imports; no longer used
// as a live destination.
export const INTAKE_URL = process.env.REACT_APP_INTAKE_URL || "#";

// Global "Start now" CTA handler used across the site. Sends the patient
// to the internal Choose Your Treatment page.
export function goToIntake() {
  if (typeof window === "undefined") return;
  try {
    window.posthog?.capture?.("cta_start_choose_treatment");
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[intake] posthog capture failed:", err);
    }
  }
  // Meta Pixel: user is entering the intake funnel from a site-wide CTA
  // ("Start Now" / "Get Started"). Fire before navigation so the event is
  // sent even if the redirect starts immediately.
  if (typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", { content_name: "choose_treatment" });
  }
  window.location.href = CHOOSE_TREATMENT_PATH;
}

// Product-specific handler used by the treatment cards on Choose Your
// Treatment and the "Start now" button on each Treatment Detail page.
export function goToProductIntake(slug) {
  if (typeof window === "undefined") return;
  const url = PRODUCT_INTAKE_URLS[slug];
  try {
    window.posthog?.capture?.("cta_start_product_intake", { slug });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[intake] posthog capture failed:", err);
    }
  }
  // Meta Pixel: product-specific InitiateCheckout with the slug as content id.
  if (typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", { content_ids: [slug], content_name: slug });
  }
  if (url) {
    // GA4: fire a generic `start_intake` event just before navigating off-site
    // to start.melbiwellness.com. Intentionally sends no custom parameters —
    // no treatment, slug, product name, or PHI.
    if (typeof window.gtag === "function") {
      try {
        window.gtag("event", "start_intake");
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.debug("[intake] gtag start_intake failed:", err);
        }
      }
    }
    window.location.href = url;
  } else if (process.env.NODE_ENV !== "production") {
    // Bask intake link not yet configured — dev-only warning.
    console.warn(`[intake] No Bask intake URL configured for slug: ${slug}`);
  }
}
